import { supabase } from './supabase';

/**
 * Validates a file to ensure it's a safe image
 * @param {File} file - The file to validate
 * @returns {Promise<boolean>} - True if valid, throws error if invalid
 */
export const validateImageSecurity = async (file) => {
  // Check file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('La taille maximale du fichier est de 5 Mo');
  }

  // Check file type using both MIME type and extension
  const validMimeTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
  if (!validMimeTypes.includes(file.type)) {
    throw new Error('Format de fichier non supporté. Utilisez JPEG, PNG, JPG ou GIF.');
  }

  // Verify file extension
  const fileName = file.name.toLowerCase();
  const validExtensions = ['.jpg', '.jpeg', '.png', '.gif'];
  if (!validExtensions.some(ext => fileName.endsWith(ext))) {
    throw new Error('Extension de fichier non supportée');
  }

  // Read file header to verify it's actually an image
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = (e) => {
      try {
        const arr = new Uint8Array(e.target.result);
        
        // Check file signatures for common image formats
        // JPEG starts with FF D8 FF
        // PNG starts with 89 50 4E 47 0D 0A 1A 0A
        // GIF starts with either GIF87a (47 49 46 38 37 61) or GIF89a (47 49 46 38 39 61)
        
        const isJpeg = arr[0] === 0xFF && arr[1] === 0xD8 && arr[2] === 0xFF;
        const isPng = arr[0] === 0x89 && arr[1] === 0x50 && arr[2] === 0x4E && arr[3] === 0x47;
        const isGif = arr[0] === 0x47 && arr[1] === 0x49 && arr[2] === 0x46 && arr[3] === 0x38 && 
                     (arr[4] === 0x37 || arr[4] === 0x39) && arr[5] === 0x61;
        
        if (isJpeg || isPng || isGif) {
          resolve(true);
        } else {
          reject(new Error('Le fichier n\'est pas une image valide'));
        }
      } catch (error) {
        reject(new Error('Erreur lors de l\'analyse du fichier'));
      }
    };
    reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'));
    reader.readAsArrayBuffer(file.slice(0, 8)); // Read just the first 8 bytes for signature
  });
};

/**
 * Uploads a profile photo to Supabase storage
 * @param {File} file - The file to upload
 * @param {string} userId - The user ID to associate with the file
 * @returns {Promise<string>} - The public URL of the uploaded file
 */
export const uploadProfilePhoto = async (file, userId) => {
  // Validate file security
  await validateImageSecurity(file);

  try {
    // Generate a unique file name with user ID and timestamp
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = fileName;

    // Upload the file
    const { data, error: uploadError } = await supabase.storage
      .from('user-avatars')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      console.error('Erreur lors du téléchargement:', uploadError);
      
      if (uploadError.statusCode === 404) {
        throw new Error('Le bucket de stockage "user-avatars" n\'existe pas. Veuillez contacter l\'administrateur pour créer ce bucket.');
      }
      
      throw uploadError;
    }

    // Get the public URL
    const { data: urlData } = supabase.storage
      .from('user-avatars')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Deletes an old profile photo from Supabase storage
 * @param {string} fileUrl - The public URL of the file to delete
 * @returns {Promise<void>}
 */
export const deleteOldProfilePhoto = async (fileUrl) => {
  if (!fileUrl) return;

  try {
    // Extract the file path from the URL
    const storageUrl = supabase.storage.from('user-avatars').getPublicUrl('').data.publicUrl;
    const filePath = fileUrl.replace(storageUrl, '');
    
    if (filePath) {
      // Delete the file
      const { error } = await supabase.storage
        .from('user-avatars')
        .remove([filePath]);

      if (error) {
        console.error('Error deleting old profile photo:', error);
      }
    }
  } catch (error) {
    console.error('Error processing delete operation:', error);
  }
}; 